import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useWebSocket } from './useWebSocket';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuthStore } from '@/store/authStore';

export function useRealtimeSync() {
  const { isConnected, lastMessage } = useWebSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!lastMessage) return;

    const { event } = lastMessage;
    const payload = lastMessage.data as
      | {
          type?: string;
          title?: string;
          message?: string;
          roles?: string[];
          custodianId?: number | null;
        }
      | undefined;

    switch (event) {
      case 'sale.created':
        queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        break;

      case 'sale.updated':
        queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        break;

      case 'sale.refunded':
        queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        toast('Sale refunded', { icon: '↩' });
        break;

      case 'sale.voided':
        queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        break;

      case 'quote.created':
        queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all });
        break;

      case 'quote.updated':
        queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all });
        break;

      case 'quote.converted':
        queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        break;

      case 'stock.updated':
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        break;

      case 'product.updated':
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        break;

      case 'settings.updated':
        queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
        toast.success('Settings updated');
        break;

      case 'notification.created': {
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        const roles = payload?.roles;
        const user = useAuthStore.getState().user;
        const role = user?.role;
        const targeted = !roles || roles.length === 0 || (!!role && roles.includes(role));
        if (!targeted) break;

        const custodianId = payload?.custodianId;
        if (custodianId != null && user?.id !== custodianId) break;

        const isPayment = payload?.type === 'payment_evidence';
        const isSale = payload?.type === 'sale_created';
        const isQuote = payload?.type === 'quote_created';
        if (isPayment) {
          queryClient.invalidateQueries({ queryKey: ['payment-evidence'] });
        }
        const icon = isPayment ? '💳' : isSale ? '💰' : isQuote ? '📄' : '📦';
        toast(payload?.title ?? 'New notification', { icon });
        break;
      }

      default:
        break;
    }
  }, [lastMessage, queryClient]);

  return { isConnected };
}
